import { useState } from 'react'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { schemaChangementMotDePasse } from '@afs/shared'
import { Bandeau } from '../../../shared/ui/Bandeau.js'
import { Bouton } from '../../../shared/ui/Bouton.js'
import { Champ } from '../../../shared/ui/Champ.js'
import { estErreurApi } from '../../../shared/api/erreurs.js'
import { reinitialiserMotDePasse } from '../api.js'
import styles from './Panneau.module.css'

/**
 * Écran ouvert depuis le lien reçu par courriel.
 *
 * Le jeton est dans l'URL, et c'est le seul endroit du socle où un secret y
 * figure — un lien de courriel n'a pas d'autre moyen de le transporter. D'où
 * les contreparties, portées par le serveur : usage unique, trente minutes de
 * validité, et toutes les sessions du compte fermées à l'arrivée.
 *
 * Ce que ce lien ne fait PAS : lever le second facteur. Reprendre la main sur
 * une boîte mail ne doit pas suffire à franchir les deux facteurs.
 */
export function ReinitialisationMotDePasse(): React.JSX.Element {
  const { jeton } = useParams<{ jeton: string }>()

  // Un lien tronqué au copier-coller arrive ici sans jeton : mieux vaut
  // renvoyer vers la demande que d'afficher un formulaire voué à l'échec.
  if (jeton === undefined || jeton === '') {
    return <Navigate to="/mot-de-passe-oublie" replace />
  }

  return <Formulaire jeton={jeton} />
}

/**
 * Séparé pour que le jeton soit une donnée d'entrée, pas une valeur dont il
 * faudrait rappeler à chaque usage qu'elle a déjà été vérifiée.
 */
function Formulaire({ jeton }: { readonly jeton: string }): React.JSX.Element {
  const naviguer = useNavigate()

  const [motDePasse, setMotDePasse] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreurLocale, setErreurLocale] = useState<string | undefined>(undefined)

  const envoi = useMutation({ mutationFn: reinitialiserMotDePasse })

  function soumettre(evenement: React.SyntheticEvent): void {
    evenement.preventDefault()

    if (motDePasse !== confirmation) {
      setErreurLocale('Les deux saisies diffèrent.')
      return
    }

    const valide = schemaChangementMotDePasse.safeParse({ motDePasse })
    if (!valide.success) {
      setErreurLocale(z.flattenError(valide.error).fieldErrors.motDePasse?.[0])
      return
    }

    setErreurLocale(undefined)
    envoi.mutate({ jeton, motDePasse })
  }

  if (envoi.isSuccess) {
    return (
      <div className={styles.ecran}>
        <div className={styles.carte}>
          <div className={styles.entete}>
            <h1 className={styles.titre}>Mot de passe enregistré</h1>
            <p className={styles.sousTitre}>
              {envoi.data.compteVerrouille
                ? // Le dire ici évite un second échec incompréhensible : la
                  // connexion refuserait sans que rien n'explique pourquoi.
                  'Votre compte est verrouillé après trop de tentatives échouées. Seul un administrateur peut le rouvrir — contactez-le, votre nouveau mot de passe est déjà en place.'
                : 'Vous pouvez vous connecter. Toutes vos sessions ouvertes ailleurs ont été fermées.'}
            </p>
          </div>

          <Bouton
            pleineLargeur
            onClick={() => {
              void naviguer('/connexion', { replace: true })
            }}
          >
            Aller à la connexion
          </Bouton>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.ecran}>
      <form className={styles.carte} onSubmit={soumettre} noValidate>
        <div className={styles.entete}>
          <h1 className={styles.titre}>Choisissez un nouveau mot de passe</h1>
          <p className={styles.sousTitre}>
            Ce lien ne fonctionne qu’une fois. Vos sessions ouvertes ailleurs seront fermées.
          </p>
        </div>

        {envoi.error && (
          <Bandeau
            ton="erreur"
            reference={estErreurApi(envoi.error) ? envoi.error.regle : undefined}
          >
            {envoi.error instanceof Error ? envoi.error.message : 'Réinitialisation impossible.'}
          </Bandeau>
        )}

        <Champ
          libelle="Nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={motDePasse}
          aide="Douze caractères au minimum, dont une majuscule, une minuscule et un chiffre."
          erreur={erreurLocale}
          onChange={(evenement) => {
            setMotDePasse(evenement.target.value)
          }}
        />

        <Champ
          libelle="Confirmez le mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(evenement) => {
            setConfirmation(evenement.target.value)
          }}
        />

        <Bouton type="submit" pleineLargeur enCours={envoi.isPending}>
          Enregistrer
        </Bouton>

        <Link to="/mot-de-passe-oublie" className={styles.lien}>
          Demander un nouveau lien
        </Link>
      </form>
    </div>
  )
}
